import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { friendsApi } from '../../lib/api';
import { toast } from 'sonner';
import { io } from 'socket.io-client';
import { playNotificationSound } from '../../lib/notificationSound';
import { usePresence } from '../../contexts/PresenceContext';
import { MessageSquare, Send, Check, CheckCheck } from 'lucide-react';

const formatTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};

const PrivateChatDialog = ({ open, onOpenChange, friendshipId, isItalian, currentUserId }) => {
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [otherTyping, setOtherTyping] = useState(false);
  const [peerLastReadAt, setPeerLastReadAt] = useState(null);
  const scrollRef = useRef(null);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const otherTypingTimeoutRef = useRef(null);
  const localTypingActiveRef = useRef(false);
  const { isOnline, emitTyping, onTyping, joinRoom, leaveRoom } = usePresence();

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const fetchChat = useCallback(async () => {
    if (!friendshipId) return;
    setLoading(true);
    try {
      const res = await friendsApi.getChat(friendshipId);
      setChat(res.data);
      setPeerLastReadAt(res.data?.peer_last_read_at || null);
      setTimeout(scrollToBottom, 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [friendshipId, isItalian, onOpenChange, scrollToBottom]);

  useEffect(() => {
    if (!open || !friendshipId) return;
    fetchChat();

    const BACKEND_URL = window.location.origin;
    const socket = io(BACKEND_URL, {
      path: '/api/sio/',
      transports: ['polling', 'websocket'],
      reconnection: true,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join_room', { room: `private_chat_${friendshipId}` });
    });

    socket.on('private_chat_message', (msg) => {
      if (msg?.friendship_id !== friendshipId) return;
      const isOwn = msg?.sender_id === currentUserId;
      setChat(prev => {
        if (!prev) return prev;
        if ((prev.messages || []).some(m => m.message_id === msg.message_id)) return prev;
        return { ...prev, messages: [...(prev.messages || []), msg] };
      });
      try { friendsApi.markRead(friendshipId); } catch { /* ignore */ }
      if (!isOwn) playNotificationSound({ pitch: 'low' });
      setTimeout(scrollToBottom, 50);
    });

    socket.on('private_chat_read', (payload) => {
      if (payload?.friendship_id !== friendshipId) return;
      if (payload?.reader_id === currentUserId) return; // self-read echo
      if (payload?.last_read_at) setPeerLastReadAt(payload.last_read_at);
    });

    return () => {
      try { socket.disconnect(); } catch { /* ignore */ }
      socketRef.current = null;
    };
  }, [open, friendshipId, fetchChat, currentUserId, scrollToBottom]);

  // Listen for "typing" events from the other user via the global presence socket
  useEffect(() => {
    if (!open || !friendshipId) return;
    const room = `private_chat_${friendshipId}`;
    // Make sure the presence (global) socket is in the room so it actually
    // receives the room-scoped `typing` broadcasts from the backend.
    joinRoom(room);
    const off = onTyping((payload) => {
      if (payload?.room !== room) return;
      if (payload?.user_id === currentUserId) return;
      if (payload?.state === 'start') {
        setOtherTyping(true);
        if (otherTypingTimeoutRef.current) clearTimeout(otherTypingTimeoutRef.current);
        otherTypingTimeoutRef.current = setTimeout(() => setOtherTyping(false), 4000);
      } else {
        setOtherTyping(false);
      }
    });
    return () => {
      off();
      leaveRoom(room);
      if (otherTypingTimeoutRef.current) clearTimeout(otherTypingTimeoutRef.current);
      setOtherTyping(false);
    };
  }, [open, friendshipId, currentUserId, onTyping, joinRoom, leaveRoom]);

  const handleDraftChange = (val) => {
    setDraft(val);
    if (!friendshipId) return;
    const room = `private_chat_${friendshipId}`;
    if (val.length > 0) {
      if (!localTypingActiveRef.current) {
        localTypingActiveRef.current = true;
        emitTyping(room, 'start');
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        localTypingActiveRef.current = false;
        emitTyping(room, 'stop');
      }, 2500);
    } else {
      if (localTypingActiveRef.current) {
        localTypingActiveRef.current = false;
        emitTyping(room, 'stop');
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await friendsApi.sendMessage(friendshipId, text);
      setDraft('');
      // Stop typing indicator
      if (localTypingActiveRef.current && friendshipId) {
        localTypingActiveRef.current = false;
        emitTyping(`private_chat_${friendshipId}`, 'stop');
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      setChat(prev => {
        if (!prev) return prev;
        // Dedup: socket may have already injected the message
        if ((prev.messages || []).some(m => m.message_id === res.data.message_id)) return prev;
        return { ...prev, messages: [...(prev.messages || []), res.data] };
      });
      setTimeout(scrollToBottom, 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? 'Errore invio' : 'Send error'));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const messages = chat?.messages || [];
  const friend = chat?.friend;
  const friendOnline = friend ? isOnline(friend.user_id) : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-2xl p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b border-white/10 bg-[#050508]">
          <DialogTitle className="font-heading text-base uppercase tracking-wider flex items-center gap-2">
            <MessageSquare size={18} className="text-[#00F0FF]" />
            <span className="truncate">{friend?.gamertag || friend?.name || '...'}</span>
            {friendOnline && (
              <span className="flex items-center gap-1 ml-2">
                <span className="w-2 h-2 rounded-full bg-[#00E676]" />
                <span className="text-[10px] text-[#00E676] normal-case tracking-normal">online</span>
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#0a0d15]"
          style={{ minHeight: 320, maxHeight: 'calc(70vh - 180px)' }}
          data-testid="private-chat-messages"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-slate-500 py-12 text-sm">
              {isItalian ? 'Nessun messaggio. Scrivi per primo!' : 'No messages yet. Start chatting!'}
            </div>
          ) : (
            messages.map(m => {
              const isMine = m.sender_id === currentUserId;
              const wasRead = isMine && peerLastReadAt && m.created_at && peerLastReadAt >= m.created_at;
              return (
                <div key={m.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[78%] rounded-lg px-3 py-2 border ${
                    isMine ? 'bg-[#00F0FF]/10 border-[#00F0FF]/30' : 'bg-[#050508] border-white/10'
                  }`}>
                    <div className="text-sm text-white whitespace-pre-wrap break-words">{m.content}</div>
                    <div className="text-[10px] text-slate-500 mt-1 text-right flex items-center justify-end gap-1">
                      <span>{formatTime(m.created_at)}</span>
                      {isMine && (
                        wasRead
                          ? <CheckCheck size={12} className="text-[#00F0FF]" data-testid="receipt-read" />
                          : <Check size={12} className="text-slate-500" data-testid="receipt-sent" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="p-3 border-t border-white/10 bg-[#050508]">
          {otherTyping && (
            <div className="text-xs text-[#00F0FF] mb-2 flex items-center gap-2" data-testid="typing-indicator">
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '120ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '240ms' }} />
              </span>
              <span>{isItalian ? `${friend?.gamertag || friend?.name || 'L\'utente'} sta scrivendo...` : `${friend?.gamertag || friend?.name || 'User'} is typing...`}</span>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={draft}
              onChange={(e) => handleDraftChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isItalian ? 'Scrivi un messaggio…' : 'Type a message…'}
              className="bg-[#0a0d15] border-white/10 text-white"
              disabled={sending || loading}
              maxLength={2000}
              data-testid="private-chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!draft.trim() || sending || loading}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black font-bold shrink-0"
              data-testid="private-chat-send"
            >
              <Send size={16} />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrivateChatDialog;
